import React, { useEffect, useState } from 'react';
import { ListenerRole } from '../../../../hooks/usePeers';
import { getAdminProfilePhotoState } from '../../../../utils/profilePhoto';
import AvatarReview from './avatar-review';
import AboutMeReview from './about-me-review';
import BackgroundTagsReview from './background-tags-review';
import DisplayNameReview from './display-name-review';
import PeerContentTile from './peer-content-tile';

interface PeerPresenceProps {
  peerData: ListenerRole;
  refetchPeerData: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const PeerPresence: React.FC<PeerPresenceProps> = ({ peerData, refetchPeerData, onDirtyChange }) => {
  const peerId = peerData.id;
  const summaryWhenOk = 'No action needed';

  const photoState = getAdminProfilePhotoState(peerData);
  const avatarNeedsAttention = photoState === 'pending_review' || photoState === 'no_photo';

  const displayNameCurrent = (peerData.user?.display_name ?? '').trim();
  const displayNameProposed = (peerData.user?.proposed_display_name ?? '').trim();
  const displayNameMissing = !displayNameCurrent && !displayNameProposed;
  const displayNameNeedsAttention =
    !!peerData.user?.display_name_rejected || !!displayNameProposed || displayNameMissing;
  const displayNameAttentionSummary = displayNameMissing ? 'No display name submitted' : undefined;

  const aboutMeNeedsAttention =
    peerData.about_me_rejected ||
    (!!peerData.proposed_about_me &&
      !(peerData.proposed_about_me === peerData.about_me && !!peerData.proposed_about_me));

  const ungradedTagCount = peerData.background_tags?.filter((tag) => tag.status === 'UNGRADED').length ?? 0;
  const backgroundTagsNeedsAttention = ungradedTagCount > 0;

  const backgroundTagsSummaryOk = !peerData.background_tags?.length ? 'No tags' : 'All approved';

  const hasPendingItems =
    avatarNeedsAttention || displayNameNeedsAttention || aboutMeNeedsAttention || backgroundTagsNeedsAttention;

  const [outerOpen, setOuterOpen] = useState<boolean>(() => hasPendingItems);

  useEffect(() => {
    setOuterOpen(hasPendingItems);
  }, [peerId, hasPendingItems]);

  const outerSummaryText = hasPendingItems ? 'Needs review' : 'No pending items';
  const outerSummaryEmphasized = hasPendingItems && !outerOpen;
  const outerSummaryClassName = outerSummaryEmphasized ? 'text-sm font-semibold text-red' : 'text-sm text-gray-600';

  return (
    <div className="rounded-lg border border-gray-300 bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-gray-50"
        onClick={() => setOuterOpen((prev) => !prev)}
      >
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-bold text-gray-900">Peer Content</h3>
          {hasPendingItems && (
            <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-800">
              Pending Review
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className={outerSummaryClassName}>{outerSummaryText}</span>
          <svg
            className={`h-5 w-5 shrink-0 transform transition-transform ${outerOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {outerOpen && (
        <div className="border-t border-gray-200 p-4">
          <div className="grid gap-3 md:grid-cols-2 md:items-start">
            <PeerContentTile
              title="Avatar Review"
              needsAttention={avatarNeedsAttention}
              summaryWhenOk={summaryWhenOk}
              peerId={peerId}
            >
              <div className="min-w-0">
                <AvatarReview peerData={peerData} refetchPeerData={refetchPeerData} onDirtyChange={onDirtyChange} />
              </div>
            </PeerContentTile>

            <PeerContentTile
              title="Display Name Review"
              needsAttention={displayNameNeedsAttention}
              attentionSummary={displayNameAttentionSummary}
              summaryWhenOk={summaryWhenOk}
              peerId={peerId}
            >
              <div className="min-w-0">
                <DisplayNameReview
                  peerData={peerData}
                  refetchPeerData={refetchPeerData}
                  onDirtyChange={onDirtyChange}
                />
              </div>
            </PeerContentTile>

            <div className="min-w-0 self-start md:col-span-2">
              <PeerContentTile
                title="About Me Review"
                needsAttention={aboutMeNeedsAttention}
                summaryWhenOk={summaryWhenOk}
                peerId={peerId}
              >
                <div className="min-w-0">
                  <AboutMeReview peerData={peerData} refetchPeerData={refetchPeerData} onDirtyChange={onDirtyChange} />
                </div>
              </PeerContentTile>
            </div>

            <div className="min-w-0 self-start md:col-span-2">
              <PeerContentTile
                title="Background Tags Review"
                needsAttention={backgroundTagsNeedsAttention}
                summaryWhenOk={backgroundTagsSummaryOk}
                peerId={peerId}
              >
                <div className="min-w-0">
                  <BackgroundTagsReview peerData={peerData} refetchPeerData={refetchPeerData} />
                </div>
              </PeerContentTile>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeerPresence;
